/**
 * Tasks (SPEC §5.5). The status of a block is its prefix: `TODO ` → todo, `DOING ` → doing,
 * `DONE ` → done, `CANCELLED ` → cancelled. One source of truth, readable in the raw text and in
 * exports (Logseq habit). A prefixed block is a #task instance without writing `#task`; a block
 * tagged `#task` without a prefix is an open task (todo).
 */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { parse } from './markdown.ts'
import type { Node } from './model.ts'

export type TaskStatus = 'todo' | 'doing' | 'done' | 'cancelled'
export const TASK_STATUSES: readonly TaskStatus[] = ['todo', 'doing', 'done', 'cancelled']

const PREFIX: Record<TaskStatus, string> = { todo: 'TODO', doing: 'DOING', done: 'DONE', cancelled: 'CANCELLED' }
const PREFIX_RE = /^(TODO|DOING|DONE|CANCELLED|CANCELED)(?: |$)/

/** True if the content starts with a task prefix (a regex test, no parsing). */
export const hasTaskPrefix = (content: string): boolean => PREFIX_RE.test(content)

/** The status the content's prefix gives, or null. */
export function prefixStatus(content: string): TaskStatus | null {
  const t = parse(content).task
  return t === null ? null : (t.toLowerCase() as TaskStatus)
}

export const isOpenStatus = (s: TaskStatus | null): boolean => s === 'todo' || s === 'doing'

/** The content with its prefix set to `status` (null removes it); the rest is untouched. */
export function withStatus(content: string, status: TaskStatus | null): string {
  const rest = content.replace(PREFIX_RE, '')
  return status === null ? rest : `${PREFIX[status]} ${rest}`
}

/** ⌘⏎ (SPEC §6.3): none → TODO → DOING → DONE → none. Cancelled goes back to TODO. */
export function cycleStatus(content: string): string {
  const s = prefixStatus(content)
  const next: TaskStatus | null = s === null ? 'todo' : s === 'todo' ? 'doing' : s === 'doing' ? 'done' : s === 'done' ? null : 'todo'
  return withStatus(content, next)
}

/** The checkbox: open → done, done or cancelled → todo. */
export function toggleDone(content: string): string {
  const s = prefixStatus(content)
  return withStatus(content, s === 'done' || s === 'cancelled' ? 'todo' : 'done')
}

/**
 * The status of a node as a task: its prefix, or todo if it carries the task tag (in its text or
 * its tags) without one; null if it is not a task.
 */
export function taskStatusOf(n: Node, isTaskTagged: (n: Node) => boolean): TaskStatus | null {
  return prefixStatus(n.content) ?? (isTaskTagged(n) ? 'todo' : null)
}

/**
 * Live tasks, kept current from graph change sets (the open-task strip and `#task` queries read
 * this instead of scanning the graph).
 */
export class TaskIndex {
  private readonly g: Graph
  private readonly isTaskTagged: (n: Node) => boolean
  private readonly ids = new Set<NodeId>()
  private readonly listeners = new Set<() => void>()
  version = 0

  constructor(g: Graph, isTaskTagged: (n: Node) => boolean) {
    this.g = g
    this.isTaskTagged = isTaskTagged
    for (const n of g.nodes()) if (this.isTask(n)) this.ids.add(n.id)
    g.subscribe((cs) => {
      let changed = false
      for (const id of cs.nodes) {
        const n = g.get(id)
        const now = n !== undefined && this.isTask(n)
        const was = this.ids.has(id)
        if (now) this.ids.add(id)
        else this.ids.delete(id)
        // A task that changed (text, fields) also counts: the strip shows it.
        if (now || was) changed = true
      }
      if (changed) {
        this.version++
        this.listeners.forEach((f) => f())
      }
    })
  }

  private isTask(n: Node): boolean {
    return n.deletedAt === null && (PREFIX_RE.test(n.content) || this.isTaskTagged(n))
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getVersion = (): number => this.version

  /** Every live task node. */
  all(): Node[] {
    const out: Node[] = []
    for (const id of this.ids) {
      const n = this.g.get(id)
      if (n && n.deletedAt === null) out.push(n)
    }
    return out
  }

  statusOf(n: Node): TaskStatus | null {
    return taskStatusOf(n, this.isTaskTagged)
  }
}
